import { useEffect, useState } from 'react';
import { deleteSetting, getSettings, putSetting, getBlurStatus, startBlurBackfill, type AdminSetting } from '../api';
import { fmtDate } from '../format';
import { useApi } from '../useApi';

/** Generate BlurHash blur-up placeholders for existing/imported photos that
    predate the feature (new uploads already get one). Polls while running. */
function BlurBackfillCard() {
  const { data, reload } = useApi(getBlurStatus);
  const [busy, setBusy] = useState(false);

  // Poll while a backfill is in progress so the counts tick down live.
  useEffect(() => {
    if (!data?.running) return;
    const id = setInterval(reload, 2000);
    return () => clearInterval(id);
  }, [data?.running, reload]);

  const run = async () => {
    setBusy(true);
    try {
      await startBlurBackfill();
      reload();
    } finally {
      setBusy(false);
    }
  };

  const missing = data?.missing ?? 0;
  const running = Boolean(data?.running);
  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Photo blur placeholders</span>
      </div>
      <p className="hint" style={{ margin: '0 0 12px' }}>
        Generates the blur-up placeholder for existing &amp; imported photos (new uploads already get one).
        Runs in the background; safe to leave.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span className="muted">
          {running
            ? `Running… ${data?.done ?? 0} done${data?.failed ? `, ${data.failed} skipped` : ''}, ${missing} left`
            : missing > 0
              ? `${missing} photo${missing === 1 ? '' : 's'} missing a placeholder`
              : 'All photos have a placeholder ✓'}
        </span>
        <button className="btn btn-primary" disabled={busy || running || missing === 0} onClick={run}>
          {running ? 'Generating…' : 'Generate'}
        </button>
      </div>
    </div>
  );
}

/** Friendly editor for the growth levers (referral reward + new-user trial). The
    same keys are editable raw below; this just gives them labels + a plan picker. */
function GrowthCard({ rows, onSaved }: { rows: AdminSetting[]; onSaved: () => void }) {
  const get = (k: string, d: string) => rows.find((r) => r.key === k)?.value ?? d;
  const seed = () => ({
    refEnabled: get('referral.enabled', 'true') === 'true',
    refKind: get('referral.reward_kind', 'sparks'),
    refSparks: get('referral.referrer_sparks', '3'),
    invSparks: get('referral.invitee_sparks', '2'),
    rewardInvitee: get('referral.reward_invitee', 'true') === 'true',
    refPlan: get('referral.premium_plan_id', 'spark'),
    refDays: get('referral.premium_days', '3'),
    trialEnabled: get('trial.enabled', 'false') === 'true',
    trialPlan: get('trial.plan_id', 'spark'),
    trialDays: get('trial.days', '3'),
  });
  const [d, setD] = useState(seed);
  // Reseed when the settings load / after a save reloads them.
  useEffect(() => { setD(seed()); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rows]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const set = <K extends keyof ReturnType<typeof seed>>(k: K, v: ReturnType<typeof seed>[K]) =>
    setD((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true); setErr(null); setSaved(false);
    try {
      await putSetting('referral.enabled', d.refEnabled ? 'true' : 'false');
      await putSetting('referral.reward_kind', d.refKind === 'premium' ? 'premium' : 'sparks');
      await putSetting('referral.referrer_sparks', String(d.refSparks));
      await putSetting('referral.invitee_sparks', String(d.invSparks));
      await putSetting('referral.reward_invitee', d.rewardInvitee ? 'true' : 'false');
      await putSetting('referral.premium_plan_id', d.refPlan.trim());
      await putSetting('referral.premium_days', String(d.refDays));
      await putSetting('trial.enabled', d.trialEnabled ? 'true' : 'false');
      await putSetting('trial.plan_id', d.trialPlan.trim());
      await putSetting('trial.days', String(d.trialDays));
      setSaved(true);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const num = (v: string, on: (s: string) => void) => (
    <input className="input" type="number" min={0} value={v} onChange={(e) => on(e.target.value)} style={{ maxWidth: 120 }} />
  );

  return (
    <div className="card">
      <div className="card-head"><span className="card-title">Growth — referral &amp; trial</span></div>
      <p className="hint" style={{ marginTop: 0 }}>
        Reward sent when an <strong>invited friend finishes onboarding</strong> (incomplete signups never pay out), plus an
        optional free trial for every new user. Plan id is one of your tier ids (<code>spark</code>, <code>pulse</code>,
        <code>infinity</code>).
      </p>

      <div className="form-grid">
        <div className="field">
          <label className="check"><input type="checkbox" checked={d.refEnabled} onChange={(e) => set('refEnabled', e.target.checked)} /> Referral rewards enabled</label>
        </div>
        <div className="field">
          <label>Reward type</label>
          <select className="input" value={d.refKind} onChange={(e) => set('refKind', e.target.value)} style={{ maxWidth: 220 }}>
            <option value="sparks">Bonus sparks</option>
            <option value="premium">Free premium days</option>
          </select>
        </div>
        {d.refKind === 'premium' ? (
          <>
            <div className="field"><label>Premium plan id</label><input className="input" value={d.refPlan} onChange={(e) => set('refPlan', e.target.value)} style={{ maxWidth: 220 }} /></div>
            <div className="field"><label>Premium days per invite</label>{num(d.refDays, (v) => set('refDays', v))}</div>
          </>
        ) : (
          <>
            <div className="field"><label>Sparks to inviter</label>{num(d.refSparks, (v) => set('refSparks', v))}</div>
            <div className="field"><label>Sparks to invited friend</label>{num(d.invSparks, (v) => set('invSparks', v))}</div>
          </>
        )}
        <div className="field">
          <label className="check"><input type="checkbox" checked={d.rewardInvitee} onChange={(e) => set('rewardInvitee', e.target.checked)} /> Also reward the invited friend</label>
        </div>

        <div className="field" style={{ marginTop: 8 }}>
          <label className="check"><input type="checkbox" checked={d.trialEnabled} onChange={(e) => set('trialEnabled', e.target.checked)} /> Free trial for new users</label>
          <span className="hint">Granted once when a brand-new user finishes onboarding.</span>
        </div>
        {d.trialEnabled ? (
          <>
            <div className="field"><label>Trial plan id</label><input className="input" value={d.trialPlan} onChange={(e) => set('trialPlan', e.target.value)} style={{ maxWidth: 220 }} /></div>
            <div className="field"><label>Trial days</label>{num(d.trialDays, (v) => set('trialDays', v))}</div>
          </>
        ) : null}
      </div>

      {err && <p className="error-line">{err}</p>}
      <button className="btn btn-primary" style={{ marginTop: 12 }} disabled={busy} onClick={() => void save()}>
        {busy ? 'Saving…' : saved ? 'Saved ✓' : 'Save growth settings'}
      </button>
    </div>
  );
}

/** Settings the API consumes live (code defaults apply while a key is unset). */
const WIRED_KEYS =
  'limits.yes_daily (30), limits.spark_free_daily (1), limits.spark_premium_daily (5), limits.messages_free_daily (100), limits.free_boosts_per_week (0), boost.duration_min (30), boost.price_ton (1), boost.price_stars (250)';

function useAction() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (id: string, fn: () => Promise<unknown>, then: () => void) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      then();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusyId(null);
    }
  };
  return { busyId, error, run };
}

function SettingRow({
  setting,
  busy,
  onSave,
  onDelete,
}: {
  setting: AdminSetting;
  busy: boolean;
  onSave: (value: string, isSecret: boolean) => void;
  onDelete: () => void;
}) {
  // Secret rows never receive the stored value — the input is a "replace" field.
  const [value, setValue] = useState(setting.isSecret ? '' : (setting.value ?? ''));
  const [isSecret, setIsSecret] = useState(setting.isSecret);
  const dirty = setting.isSecret ? value !== '' : true;

  return (
    <div className="field">
      <label className="mono">{setting.key}</label>
      {setting.isSecret ? (
        <div className="form-row">
          <span className="muted mono" title="Secret — only the last 4 characters are shown">
            {setting.hint}
          </span>
          <input
            className="input"
            style={{ flex: 1, minWidth: 200 }}
            type="password"
            placeholder="Replace secret…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
      ) : (
        <input className="input" value={value} onChange={(e) => setValue(e.target.value)} />
      )}
      <div className="form-row" style={{ alignItems: 'center', marginTop: 4 }}>
        <label className="check">
          <input type="checkbox" checked={isSecret} onChange={(e) => setIsSecret(e.target.checked)} />
          Secret
        </label>
        <span className="muted" style={{ fontSize: '0.76rem' }}>Updated {fmtDate(setting.updatedAt)}</span>
        <div className="row-actions" style={{ marginLeft: 'auto' }}>
          <button
            className="btn btn-primary"
            disabled={busy || !dirty}
            title={setting.isSecret && !dirty ? 'Type a replacement value first' : undefined}
            onClick={() => onSave(value, isSecret)}
          >
            Save
          </button>
          <button className="btn btn-danger" disabled={busy} onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function Settings() {
  const { data, error, loading, reload } = useApi(getSettings);
  const { busyId, error: actionError, run } = useAction();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newSecret, setNewSecret] = useState(false);
  const rows = data?.data ?? [];

  const create = () =>
    void run(
      'new',
      () => putSetting(newKey.trim(), newValue, newSecret),
      () => {
        setNewKey('');
        setNewValue('');
        setNewSecret(false);
        reload();
      },
    );

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Settings<span className="dot">.</span>
        </h1>
        {data && <span className="muted">{rows.length} keys</span>}
      </div>
      <p className="hint" style={{ marginBottom: 16 }}>
        Runtime config the API reads live (60s cache). Wired keys: <code>{WIRED_KEYS}</code>. Deleting a key restores the
        code default. Secret values are stored server-side and only a last-4 hint ever comes back.
      </p>
      {actionError && <p className="error-line">{actionError}</p>}

      <BlurBackfillCard />

      <GrowthCard rows={rows} onSaved={reload} />

      <div className="card">
        <div className="card-head">
          <span className="card-title">Add setting</span>
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Key</label>
            <input
              className="input"
              placeholder="limits.spark_free_daily"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Value</label>
            <input
              className="input"
              placeholder="Value"
              type={newSecret ? 'password' : 'text'}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
          </div>
        </div>
        <div className="form-row" style={{ alignItems: 'center', marginTop: 14 }}>
          <label className="check">
            <input type="checkbox" checked={newSecret} onChange={(e) => setNewSecret(e.target.checked)} />
            Secret
          </label>
          <button
            className="btn btn-primary"
            style={{ marginLeft: 'auto' }}
            disabled={busyId === 'new' || !newKey.trim()}
            onClick={create}
          >
            Add setting
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="card-title">Overrides</span>
        </div>
        {error ? (
          <div className="empty">{error}</div>
        ) : loading && !data ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No settings yet — defaults from code apply. Add an override above.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {rows.map((s) => (
              <SettingRow
                key={`${s.key}:${s.updatedAt}`}
                setting={s}
                busy={busyId === s.key}
                onSave={(value, isSecret) => void run(s.key, () => putSetting(s.key, value, isSecret), reload)}
                onDelete={() => {
                  if (!window.confirm(`Delete setting "${s.key}"? The code default applies again.`)) return;
                  void run(s.key, () => deleteSetting(s.key), reload);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
